import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { syncFromSupabase } from './utils/supabase'
import { useAutoSync } from './hooks/useAutoSync'

// REMOVIDO: migrarStatusesPorPrazo() rodava no boot de TODO dispositivo
// novo. Sobrescrevia status de todos os pedidos com base no prazo (regra
// antiga) e limpava overrides do Bling. Quando um user novo (ex: Kelvin)
// entrava pela 1a vez, a migração rodava, destruía os status e empurrava
// pro Supabase por cima dos dados corretos — a versão quebrada era vista
// por toda a equipe.
// A migração era one-time global mas checava a flag POR DISPOSITIVO
// (localStorage). Cada dispositivo novo rodava de novo.
// Como a migração original v1 já rodou faz muito tempo em produção,
// removemos a chamada. Se um dia precisarmos rodar migração de dados,
// tem que ser server-side (SQL no Supabase), não client-side.
import { Navbar } from './components/common/Navbar'
import { MobileNav } from './components/common/MobileNav'
import { ColisaoBanner } from './components/common/ColisaoBanner'
import { Login } from './pages/Login'
import { TrocarSenha } from './pages/TrocarSenha'
import { Dashboard } from './pages/Dashboard'
import { NewOrder } from './pages/NewOrder'
import { Orders } from './pages/Orders'
import { OrderDetail } from './pages/OrderDetail'
import { Kanban } from './pages/Kanban'
import { Logistica } from './pages/Logistica'
import { CalculadoraCusto } from './pages/CalculadoraCusto'
import { Config } from './pages/Config'
import { BlingCallback } from './pages/BlingCallback'
import { Dashboards } from './pages/Dashboards'
import { Diagnostico } from './pages/Diagnostico'
import { Clientes } from './pages/Clientes'
import { Comissoes } from './pages/Comissoes'
import { OportunidadesArco } from './pages/OportunidadesArco'
import { ContasAReceber } from './pages/ContasAReceber'
import { PainelTV } from './pages/PainelTV'
import { DiagnosticoFormini } from './pages/DiagnosticoFormini'
import { Marketplace } from './pages/Marketplace'
import { AdminUsuarios } from './pages/AdminUsuarios'
import { Lixeira } from './pages/Lixeira'
import { ErrorBoundary } from './components/common/ErrorBoundary'

function AppContent() {
  const { user, profile, loading } = useAuth()
  const [syncing, setSyncing] = useState(true)

  useEffect(() => {
    if (!loading) {
      if (user) syncFromSupabase().finally(() => setSyncing(false))
      else setSyncing(false)
    }
  }, [loading, user])

  // Re-sincroniza com Supabase quando o app volta ao foco (mobile:
  // sai/volta do background; desktop: tab ganha foco) e quando volta a
  // ter rede. Sem isso, vendedora deixava o app aberto no celular o dia
  // todo sem ver pedidos novos de outros usuários.
  useAutoSync({ enabled: !!user })

  if (loading || (user && syncing)) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-[#111111] gap-4">
        <img src="/logo.png" alt="Mini Deck" className="w-16 h-16 rounded-2xl" onError={e => e.target.style.display='none'} />
        <div className="w-8 h-8 border-4 border-[#F5C200] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#F5C200] text-sm font-semibold">Sincronizando dados…</p>
      </div>
    )
  }

  if (!user) return <Login />

  if (profile?.must_change_password) return <TrocarSenha />

  // Painel TV — rota dedicada de tela cheia (sem Navbar/MobileNav nem
  // padding). Faz login uma vez e fica aberto numa TV do escritório.
  // Detecta direto via window.location pra evitar render do shell antes
  // do React Router montar.
  if (window.location.pathname === '/painel-tv') {
    return (
      <ErrorBoundary onde="PainelTV">
        <PainelTV />
      </ErrorBoundary>
    )
  }

  return (
    <>
      <Navbar />
      <ColisaoBanner />
      <MobileNav />
      {/* pt-12 em mobile (top bar h-12) / pt-14 em desktop (navbar h-14)
          pb-20 em mobile pra bottom nav h-16 não tampar conteúdo / pb-0 desktop */}
      <main className="pt-12 md:pt-14 pb-20 md:pb-0">
        {/* Cada rota envolvida em ErrorBoundary — se uma quebra, as outras
            continuam funcionando, e a vendedora/gerente vê o erro em vez
            de tela branca, com botão pra copiar detalhes técnicos. */}
        <Routes>
          <Route path="/"             element={<ErrorBoundary onde="Dashboard"><Dashboard /></ErrorBoundary>} />
          <Route path="/pedido/novo"  element={<ErrorBoundary onde="NewOrder"><NewOrder /></ErrorBoundary>} />
          <Route path="/pedidos"      element={<ErrorBoundary onde="Orders"><Orders /></ErrorBoundary>} />
          <Route path="/pedidos/:id"  element={<ErrorBoundary onde="OrderDetail"><OrderDetail /></ErrorBoundary>} />
          <Route path="/kanban"       element={<ErrorBoundary onde="Kanban"><Kanban /></ErrorBoundary>} />
          <Route path="/logistica"    element={<ErrorBoundary onde="Logistica"><Logistica /></ErrorBoundary>} />
          <Route path="/custo"        element={<ErrorBoundary onde="CalculadoraCusto"><CalculadoraCusto /></ErrorBoundary>} />
          <Route path="/dashboards"   element={<ErrorBoundary onde="Dashboards"><Dashboards /></ErrorBoundary>} />
          <Route path="/metas"        element={<ErrorBoundary onde="Dashboards"><Dashboards /></ErrorBoundary>} />
          <Route path="/diagnostico"  element={<ErrorBoundary onde="Diagnostico"><Diagnostico /></ErrorBoundary>} />
          <Route path="/diagnostico-formini" element={<ErrorBoundary onde="DiagnosticoFormini"><DiagnosticoFormini /></ErrorBoundary>} />
          <Route path="/marketplace" element={<ErrorBoundary onde="Marketplace"><Marketplace /></ErrorBoundary>} />
          <Route path="/admin/usuarios" element={<ErrorBoundary onde="AdminUsuarios"><AdminUsuarios /></ErrorBoundary>} />
          <Route path="/lixeira" element={<ErrorBoundary onde="Lixeira"><Lixeira /></ErrorBoundary>} />
          <Route path="/clientes"     element={<ErrorBoundary onde="Clientes"><Clientes /></ErrorBoundary>} />
          <Route path="/comissoes"    element={<ErrorBoundary onde="Comissoes"><Comissoes /></ErrorBoundary>} />
          <Route path="/oportunidades-arco" element={<ErrorBoundary onde="OportunidadesArco"><OportunidadesArco /></ErrorBoundary>} />
          <Route path="/contas-a-receber"   element={<ErrorBoundary onde="ContasAReceber"><ContasAReceber /></ErrorBoundary>} />
          <Route path="/config"       element={<ErrorBoundary onde="Config"><Config /></ErrorBoundary>} />
          <Route path="/bling/callback" element={<ErrorBoundary onde="BlingCallback"><BlingCallback /></ErrorBoundary>} />
        </Routes>
      </main>
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  )
}
